/**
 * I dati del cliente sulla fattura di un ordine del sito (biglietti degli
 * eventi). Li usa la finestra della fattura in Pixel, per dire subito cosa
 * manca mentre si scrive, e la funzione fattura-ordine-sito, che ricontrolla
 * tutto prima di salvare la scheda in Clienti e creare la fattura.
 *
 * Il percorso: si parte da una BOZZA (quello che ha scritto chi ha comprato
 * nel checkout, oppure la scheda gia' in Clienti), la si corregge a mano se
 * serve, e la si CONTROLLA. Solo una bozza che passa il controllo diventa la
 * fattura: una fattura emessa non si cancella, si corregge solo con una nota
 * di credito.
 */

/** I dati come si scrivono nel modulo: tutti testi, anche vuoti. */
export interface ClienteInserito {
  tipo: 'azienda' | 'privato';
  /** Ragione sociale, o nome e cognome per un privato. */
  denominazione: string;
  partitaIva: string;
  codiceFiscale: string;
  /** Codice destinatario: vuoto o "0000000" = nessuno. */
  codiceSdi: string;
  pec: string;
  indirizzo: string;
  cap: string;
  citta: string;
  provincia: string;
}

export type CampoCliente = keyof ClienteInserito;

export const ETICHETTE: Record<CampoCliente, string> = {
  tipo: 'Tipo',
  denominazione: 'Ragione sociale / Nome e cognome',
  partitaIva: 'Partita IVA',
  codiceFiscale: 'Codice fiscale',
  codiceSdi: 'Codice SDI',
  pec: 'PEC',
  indirizzo: 'Indirizzo',
  cap: 'CAP',
  citta: 'Città',
  provincia: 'Provincia',
};

/** I dati controllati, pronti per la scheda in Clienti e per Fatture in Cloud. */
export interface DatiFiscali {
  tipo: 'azienda' | 'privato';
  denominazione: string;
  /** Chi compra, per la scheda cliente. */
  referente: string | null;
  partitaIva: string | null;
  codiceFiscale: string | null;
  /** null = nessun codice: la fattura va alla PEC o nel cassetto fiscale. */
  codiceSdi: string | null;
  pec: string | null;
  indirizzo: string;
  cap: string;
  citta: string;
  provincia: string;
  paese: 'Italia';
  email: string | null;
  telefono: string | null;
}

/** Quello che serve dell'ordine (ordini_sito, migrazioni 035 e 037). */
export interface OrdineConDatiCliente {
  nome: string | null;
  cognome: string | null;
  azienda: string | null;
  email: string | null;
  telefono: string | null;
  indirizzo: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  paese: string | null;
  codice_fiscale: string | null;
  partita_iva: string | null;
  codice_sdi: string | null;
  pec: string | null;
}

/** Quello che serve della scheda in Clienti (customers). */
export interface SchedaConDatiCliente {
  company_name: string | null;
  vat_number: string | null;
  tax_code: string | null;
  sdi_code: string | null;
  pec_email: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  province: string | null;
}

/** Email, telefono e referente: non stanno nel modulo, arrivano dall'ordine. */
export interface Contatti {
  email: string | null;
  telefono: string | null;
  referente: string | null;
}

export type EsitoCliente =
  | { ok: true; dati: DatiFiscali; avvisi: string[] }
  | { ok: false; motivo: string; errori: Partial<Record<CampoCliente, string>>; avvisi: string[] };

// ---------------------------------------------------------------------------
// Codici
// ---------------------------------------------------------------------------

const DISPARI: Record<string, number> = {
  0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18,
  N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};

const valorePari = (ch: string): number => (/\d/.test(ch) ? Number(ch) : ch.charCodeAt(0) - 65);

/** Codice fiscale di una persona: forma (con le lettere di omocodia) e carattere di controllo. */
export function codiceFiscaleValido(cf: string): boolean {
  if (!/^[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/.test(cf)) return false;
  let somma = 0;
  for (let i = 0; i < 15; i++) somma += i % 2 === 0 ? DISPARI[cf[i]] : valorePari(cf[i]);
  return String.fromCharCode(65 + (somma % 26)) === cf[15];
}

/** Partita IVA italiana (e codice fiscale numerico di una societa'): 11 cifre e cifra di controllo. */
export function partitaIvaValida(piva: string): boolean {
  if (!/^\d{11}$/.test(piva) || piva === '00000000000') return false;
  let somma = 0;
  for (let i = 0; i < 10; i++) {
    const d = Number(piva[i]);
    if (i % 2 === 0) somma += d;
    else somma += d * 2 > 9 ? d * 2 - 9 : d * 2;
  }
  return (10 - (somma % 10)) % 10 === Number(piva[10]);
}

/** Senza spazi, punti e trattini, maiuscolo. */
export const pulisciCodice = (v: string | null | undefined): string => (v ?? '').replace(/[\s.-]/g, '').toUpperCase();

/** La P.IVA senza "IT" davanti. */
const senzaIT = (v: string): string => v.replace(/^IT(?=\d{11}$)/, '');

const EMAIL_VALIDA = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const testo = (v: string | null | undefined): string => (v ?? '').trim();

// ---------------------------------------------------------------------------
// Le bozze
// ---------------------------------------------------------------------------

/**
 * La bozza dai campi del checkout. Il checkout fino a ottobre 2026 non aveva
 * la Partita IVA: le aziende la scrivevano nel Codice fiscale o nel Codice
 * SDI. Si cerca una P.IVA valida in tutti e tre i campi; il resto si lascia
 * come scritto, cosi' chi controlla vede e corregge.
 */
export function bozzaDaOrdine(o: OrdineConDatiCliente): { bozza: ClienteInserito; avvisi: string[] } {
  const avvisi: string[] = [];
  const campoPiva = senzaIT(pulisciCodice(o.partita_iva));
  const campoCf = pulisciCodice(o.codice_fiscale);
  const campoSdi = pulisciCodice(o.codice_sdi);

  let partitaIva = '';
  for (const [valore, campo] of [[campoPiva, 'Partita IVA'], [campoCf, 'Codice fiscale'], [campoSdi, 'Codice SDI']] as const) {
    if (partitaIva || !/^\d{11}$/.test(valore)) continue;
    if (partitaIvaValida(valore)) {
      partitaIva = valore;
      if (campo === 'Codice SDI') avvisi.push(`La partita IVA ${valore} era scritta nel campo Codice SDI.`);
    } else {
      avvisi.push(`«${valore}» nel campo ${campo} non è una partita IVA valida.`);
    }
  }
  // Una P.IVA scritta in un campo che non e' il suo, ma non valida: la si
  // propone comunque nel campo giusto, il controllo la segnalera'.
  if (!partitaIva && /^\d{11}$/.test(campoPiva)) partitaIva = campoPiva;

  let codiceSdi = '';
  if (/^[A-Z0-9]{7}$/.test(campoSdi)) {
    codiceSdi = campoSdi === '0000000' ? '' : campoSdi;
  } else if (campoSdi && campoSdi !== partitaIva) {
    avvisi.push(`«${campoSdi}» nel campo Codice SDI non è un codice SDI: ignorato.`);
  }

  const persona = [o.nome, o.cognome].map(testo).filter(Boolean).join(' ');
  const azienda = testo(o.azienda);
  const tipo = partitaIva ? 'azienda' : 'privato';
  return {
    avvisi,
    bozza: {
      tipo,
      denominazione: tipo === 'azienda' ? azienda || persona : persona || azienda,
      partitaIva,
      codiceFiscale: campoCf,
      codiceSdi,
      pec: testo(o.pec).toLowerCase(),
      indirizzo: testo(o.indirizzo),
      cap: testo(o.cap).replace(/\s/g, ''),
      citta: testo(o.citta),
      provincia: testo(o.provincia).toUpperCase(),
    },
  };
}

/** La bozza dalla scheda gia' in Clienti. */
export function bozzaDaScheda(c: SchedaConDatiCliente): ClienteInserito {
  const partitaIva = senzaIT(pulisciCodice(c.vat_number));
  const sdi = pulisciCodice(c.sdi_code);
  return {
    tipo: partitaIva ? 'azienda' : 'privato',
    denominazione: testo(c.company_name),
    partitaIva,
    codiceFiscale: pulisciCodice(c.tax_code),
    codiceSdi: sdi === '0000000' ? '' : sdi,
    pec: testo(c.pec_email).toLowerCase(),
    indirizzo: testo(c.address),
    cap: testo(c.postal_code).replace(/\s/g, ''),
    citta: testo(c.city),
    provincia: testo(c.province).toUpperCase(),
  };
}

// ---------------------------------------------------------------------------
// Il controllo
// ---------------------------------------------------------------------------

/**
 * Il controllo dei dati del cliente: quelli che servono alla fattura
 * elettronica, con le cifre di controllo. Ritorna gli errori campo per campo.
 */
export function validaCliente(b: ClienteInserito, contatti: Contatti): EsitoCliente {
  const errori: Partial<Record<CampoCliente, string>> = {};
  const avvisi: string[] = [];

  const denominazione = testo(b.denominazione);
  const piva = senzaIT(pulisciCodice(b.partitaIva));
  const cf = pulisciCodice(b.codiceFiscale);
  const sdi = pulisciCodice(b.codiceSdi);
  const pec = testo(b.pec).toLowerCase();
  const indirizzo = testo(b.indirizzo);
  const cap = testo(b.cap).replace(/\s/g, '');
  const citta = testo(b.citta);
  const provincia = testo(b.provincia).toUpperCase();

  if (!denominazione) {
    errori.denominazione = b.tipo === 'azienda' ? 'Scrivi la ragione sociale.' : 'Scrivi nome e cognome.';
  }

  if (b.tipo === 'azienda') {
    if (!piva) errori.partitaIva = 'Per un\'azienda serve la partita IVA.';
    else if (!partitaIvaValida(piva)) errori.partitaIva = 'Partita IVA non valida (11 cifre, controlla di averla copiata giusta).';
    if (cf && !codiceFiscaleValido(cf) && !partitaIvaValida(cf)) errori.codiceFiscale = 'Codice fiscale non valido.';
  } else {
    if (piva) errori.partitaIva = 'Con la partita IVA scegli «Azienda».';
    if (!cf) errori.codiceFiscale = 'Per un privato serve il codice fiscale.';
    else if (!codiceFiscaleValido(cf)) errori.codiceFiscale = 'Codice fiscale non valido (16 caratteri, controlla di averlo copiato giusto).';
  }

  if (sdi && sdi !== '0000000' && !/^[A-Z0-9]{7}$/.test(sdi)) errori.codiceSdi = 'Il codice SDI ha 7 caratteri (o lascialo vuoto).';
  if (pec && !EMAIL_VALIDA.test(pec)) errori.pec = 'Indirizzo PEC non valido (o lascialo vuoto).';
  if (!indirizzo) errori.indirizzo = 'Scrivi via e numero.';
  if (!/^\d{5}$/.test(cap)) errori.cap = 'Il CAP ha 5 cifre.';
  if (!citta) errori.citta = 'Scrivi la città.';
  if (!/^[A-Z]{2}$/.test(provincia)) errori.provincia = 'Sigla della provincia, 2 lettere (es. PE).';

  const campiSbagliati = Object.keys(errori) as CampoCliente[];
  if (campiSbagliati.length > 0) {
    const mancanoCodici = !piva && !cf;
    return {
      ok: false,
      errori,
      avvisi,
      motivo: mancanoCodici
        ? 'Mancano codice fiscale e partita IVA: senza, la fattura elettronica non si può fare. Completa i dati qui sotto: finiscono nella scheda del cliente in Clienti.'
        : `Da completare o correggere: ${campiSbagliati.map((c) => ETICHETTE[c]).join(', ')}.`,
    };
  }

  const codiceSdi = sdi && sdi !== '0000000' ? sdi : null;
  if (b.tipo === 'azienda' && !codiceSdi && !pec) {
    avvisi.push('Azienda senza codice SDI né PEC: la fattura arriverà solo nel suo cassetto fiscale.');
  }

  return {
    ok: true,
    avvisi,
    dati: {
      tipo: b.tipo,
      denominazione,
      referente: contatti.referente,
      partitaIva: b.tipo === 'azienda' ? piva : null,
      codiceFiscale: cf || null,
      codiceSdi,
      pec: pec || null,
      indirizzo,
      cap,
      citta,
      provincia,
      paese: 'Italia',
      email: contatti.email,
      telefono: contatti.telefono,
    },
  };
}

/** Email, telefono e nome di chi ha comprato, dall'ordine. */
export function contattiDaOrdine(o: OrdineConDatiCliente): Contatti {
  const persona = [o.nome, o.cognome].map(testo).filter(Boolean).join(' ');
  return { email: o.email, telefono: o.telefono, referente: persona || null };
}

/** Un cliente con indirizzo fuori dall'Italia: la fattura si fa a mano. */
export const clienteEstero = (o: Pick<OrdineConDatiCliente, 'paese'>): boolean => Boolean(o.paese && o.paese !== 'IT');

/** I dati del modulo come arrivano dal browser: solo i campi noti, solo testi, tagliati. */
export function leggiClienteInserito(v: unknown): ClienteInserito | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (o.tipo !== 'azienda' && o.tipo !== 'privato') return null;
  const campo = (k: Exclude<CampoCliente, 'tipo'>): string => (typeof o[k] === 'string' ? (o[k] as string).slice(0, 200) : '');
  return {
    tipo: o.tipo,
    denominazione: campo('denominazione'),
    partitaIva: campo('partitaIva'),
    codiceFiscale: campo('codiceFiscale'),
    codiceSdi: campo('codiceSdi'),
    pec: campo('pec'),
    indirizzo: campo('indirizzo'),
    cap: campo('cap'),
    citta: campo('citta'),
    provincia: campo('provincia'),
  };
}
